import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { loads as loadsApi, vehicles as vApi, formatErr } from "@/lib/api";
import { ArrowRight, Plus, Truck, CarProfile, Barcode } from "@phosphor-icons/react";
import VinScanner from "@/components/VinScanner";
import ConfirmDelete from "@/components/ConfirmDelete";
import { toast } from "sonner";
import DriverFilter, { useDrivers, AddedBy } from "@/components/DriverFilter";
import { useAuth } from "@/lib/auth-context";
import LoadMoney, { money } from "@/components/LoadMoney";

export function LoadsList() {
  const { user } = useAuth();
  const isOwner = user?.fleet_role === "owner";
  const drivers = useDrivers(isOwner);
  const [driver, setDriver] = useState("");
  const [list, setList] = useState([]);
  useEffect(() => { loadsApi.list(driver ? { driver } : undefined).then(setList); }, [driver]);
  return <AppShell>
    <Header title="Loads" cta={<Link to="/app/loads/new" data-testid="btn-new-load" className="btn-amber"><Plus size={16} weight="bold"/> New Load</Link>}/>
    {isOwner && <div className="mb-4"><DriverFilter drivers={drivers} value={driver} onChange={setDriver} tid="loads-driver-filter"/></div>}
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {list.length === 0 && <div className="col-span-full border-2 border-dashed p-10 text-center text-muted-foreground">No loads yet.</div>}
      {list.map(l => (
        <Link key={l.id} to={`/app/loads/${l.id}`} data-testid={`load-${l.id}`} className="bg-white border border-border/60 p-5 hover:border-accent">
          <div className="flex items-center justify-between">
            <div className="font-mono-tech text-xs text-muted-foreground">#{l.load_number}</div>
            <span className={`eyebrow ${l.status === 'active' ? 'text-accent' : 'text-muted-foreground'}`}>{l.status}</span>
          </div>
          <div className="font-display font-bold text-lg mt-1">{l.broker || "No broker"}</div>
          <div className="text-sm text-muted-foreground">{l.pickup_city || "—"} → {l.delivery_city || "—"}</div>
          <div className="mt-3 flex items-center justify-between text-sm">
            <span><Truck className="inline" size={14} /> {l.vehicle_count ?? 0} vehicles</span>
            <ArrowRight size={16}/>
          </div>
          {l.finance && <div className="mt-2 flex items-center justify-between text-sm" data-testid={`load-profit-${l.id}`}>
            <span className="eyebrow text-muted-foreground">Profit</span>
            <span className={`font-display font-bold ${l.finance.profit < 0 ? "text-red-600" : "text-primary"}`}>{money(l.finance.profit)}{l.finance.profit_per_mile != null && <span className="font-mono-tech text-[11px] text-muted-foreground font-normal"> · {money(l.finance.profit_per_mile)}/mi</span>}</span>
          </div>}
          {l.added_by && <div className="mt-2 pt-2 border-t border-border/60"><AddedBy name={l.added_by} tid={`load-added-by-${l.id}`}/></div>}
        </Link>
      ))}
    </div>
  </AppShell>;
}

export function NewLoad() {
  const nav = useNavigate();
  const [form, setForm] = useState({ load_number: "", broker: "", pickup_city: "", delivery_city: "", pickup_date: "", expected_delivery_date: "", notes: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const ld = await loadsApi.create(form);
      toast.success("Load created");
      nav(`/app/loads/${ld.id}`);
    } catch (err) { toast.error(formatErr(err)); }
  };
  return <AppShell>
    <Header title="New Load" back="/app/loads"/>
    <form onSubmit={submit} className="max-w-2xl space-y-4 bg-white border border-border/60 p-6">
      <TwoCol>
        <F label="Load number (auto if blank)" tid="in-load-number" value={form.load_number} onChange={set("load_number")}/>
        <F label="Broker / Customer" tid="in-broker" value={form.broker} onChange={set("broker")}/>
      </TwoCol>
      <TwoCol>
        <F label="Pickup city" tid="in-pickup-city" value={form.pickup_city} onChange={set("pickup_city")}/>
        <F label="Delivery city" tid="in-delivery-city" value={form.delivery_city} onChange={set("delivery_city")}/>
      </TwoCol>
      <TwoCol>
        <F label="Pickup date" tid="in-pickup-date" type="date" value={form.pickup_date} onChange={set("pickup_date")}/>
        <F label="Expected delivery date" tid="in-expected-date" type="date" value={form.expected_delivery_date} onChange={set("expected_delivery_date")}/>
      </TwoCol>
      <p className="text-xs text-muted-foreground">Revenue comes from each vehicle's rate — add rates, miles and fuel/toll receipts on the load page.</p>
      <F label="Notes" tid="in-notes" value={form.notes} onChange={set("notes")} textarea/>
      <button data-testid="btn-create-load" className="btn-amber w-full">Create Load</button>
    </form>
  </AppShell>;
}

export function LoadDetail() {
  const { id } = useParams();
  const [load, setLoad] = useState(null);
  const nav = useNavigate();
  const reload = () => loadsApi.get(id).then(setLoad);
  useEffect(() => { reload(); }, [id]);
  if (!load) return <AppShell>Loading…</AppShell>;
  return <AppShell>
    <Header title={`Load #${load.load_number}`} back="/app/loads"
      cta={<button onClick={() => nav(`/app/loads/${id}/add-vehicle`)} data-testid="btn-add-vehicle" className="btn-amber"><Plus size={16} weight="bold"/> Add Vehicle</button>}/>
    <div className="grid md:grid-cols-3 gap-3 mb-6">
      <Info k="Broker" v={load.broker}/>
      <Info k="Pickup" v={load.pickup_city}/>
      <Info k="Delivery" v={load.delivery_city}/>
      <Info k="Pickup date" v={load.pickup_date}/>
      <Info k="Expected delivery" v={load.expected_delivery_date}/>
      {load.added_by && <Info k="Added by" v={<span data-testid="load-detail-added-by">{load.added_by}</span>}/>}
    </div>
    {load.can_see_money && <LoadMoney load={load} onChange={reload}/>}
    <h3 className="font-display font-bold text-xl mb-3">Vehicles on this load</h3>
    <div className="space-y-2">
      {load.vehicles?.length === 0 && <div className="border-2 border-dashed p-8 text-center text-muted-foreground">No vehicles yet. Scan the first VIN.</div>}
      {load.vehicles?.map(v => (
        <Link key={v.id} to={`/app/vehicles/${v.id}`} data-testid={`vrow-${v.id}`} className="flex items-center gap-3 bg-white border border-border/60 p-4 hover:border-accent">
          <CarProfile size={28} weight="duotone" className="text-primary"/>
          <div className="flex-1">
            <div className="font-mono-tech text-[11px] text-muted-foreground">{v.vin}</div>
            <div className="font-display font-bold">{v.year} {v.make} {v.model} <span className="text-muted-foreground font-normal text-sm">· {v.color || "—"}</span></div>
            <AddedBy name={v.added_by} tid={`vehicle-added-by-${v.id}`}/>
          </div>
          <span className="eyebrow text-accent">{v.status.replace("_"," ")}</span>
          <ArrowRight size={16}/>
        </Link>
      ))}
    </div>
    <div className="mt-10 pt-6 border-t border-border">
      <ConfirmDelete testid="btn-delete-load" label="Delete load"
        title={`Delete load #${load.load_number}?`}
        description={`This permanently removes the load${load.vehicles?.length ? `, its ${load.vehicles.length} vehicle(s) and all their inspections, photos and signatures` : ""}. This can't be undone.`}
        onConfirm={async () => {
          try { await loadsApi.del(id); toast.success("Load deleted"); nav("/app/loads"); }
          catch (e) { toast.error(formatErr(e)); }
        }}/>
    </div>
  </AppShell>;
}

export function AddVehicle() {
  const { id: loadId } = useParams();
  const nav = useNavigate();
  const [vin, setVin] = useState("");
  const [decoded, setDecoded] = useState(null);
  const [extra, setExtra] = useState({ color: "", odometer: "", stock_number: "", auction_lot: "", pickup_address: "", delivery_address: "", rate: "" });
  const [canMoney, setCanMoney] = useState(false);
  useEffect(() => { loadsApi.get(loadId).then(l => setCanMoney(!!l.can_see_money)).catch(() => {}); }, [loadId]);
  const [locAlert, setLocAlert] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);

  const decode = async (value = vin) => {
    if (value.length < 11) { toast.error("VIN too short"); return; }
    setLoading(true);
    try { const d = await (await import("@/lib/api")).vinDecode(value); setDecoded(d); if (!d.make) toast.message("Partial decode — fill the rest manually"); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  const checkLocation = async (addr) => {
    if (!addr || addr.length < 4) { setLocAlert(null); return; }
    const api = await import("@/lib/api");
    const hits = await api.locations.lookup(addr);
    setLocAlert(hits?.[0] || null);
  };

  const save = async () => {
    if (!vin || !decoded) { toast.error("Decode the VIN first"); return; }
    try {
      const v = await vApi.create({
        load_id: loadId, vin,
        year: decoded.year, make: decoded.make, model: decoded.model,
        trim: decoded.trim, body_style: decoded.body_style, vehicle_type: decoded.vehicle_type,
        color: extra.color || null,
        odometer: extra.odometer ? parseInt(extra.odometer) : null,
        stock_number: extra.stock_number || null, auction_lot: extra.auction_lot || null,
        pickup_address: extra.pickup_address || null, delivery_address: extra.delivery_address || null,
        rate: extra.rate ? parseFloat(extra.rate) : null,
      });
      toast.success("Vehicle added");
      nav(`/app/vehicles/${v.id}`);
    } catch (e) { toast.error(formatErr(e)); }
  };

  return <AppShell>
    <Header title="Add Vehicle" back={`/app/loads/${loadId}`}/>
    <div className="max-w-2xl space-y-4 bg-white border border-border/60 p-6">
      <div>
        <div className="eyebrow mb-1">VIN</div>
        <div className="flex gap-2">
          <input data-testid="in-vin" value={vin} onChange={(e)=>setVin(e.target.value.toUpperCase())}
                 placeholder="17-char VIN" className="flex-1 min-w-0 border-2 border-border focus:border-accent px-3 py-3 font-mono-tech uppercase text-base"/>
          <button data-testid="btn-decode" onClick={() => decode()} disabled={loading} className="btn-dark">{loading ? "…" : "Decode"}</button>
        </div>
        <button data-testid="btn-scan-vin" onClick={() => setScanning(true)} className="btn-amber w-full mt-2">
          <Barcode size={20} weight="bold"/> Scan VIN barcode
        </button>
      </div>
      {scanning && <VinScanner onClose={() => setScanning(false)} onDetected={(v) => {
        setScanning(false); setVin(v); toast.success(`VIN scanned: ${v}`); decode(v);
      }}/>}
      {decoded && (
        <div className="bg-secondary border border-border/60 p-4 slide-up">
          <div className="eyebrow text-accent mb-1">Decoded</div>
          <div className="font-display font-bold text-xl">{decoded.year} {decoded.make} {decoded.model}</div>
          <div className="text-sm text-muted-foreground">{decoded.trim} · {decoded.body_style} · {decoded.vehicle_type}</div>
        </div>
      )}
      <TwoCol>
        <F label="Color" tid="in-color" value={extra.color} onChange={(e)=>setExtra({...extra,color:e.target.value})}/>
        <F label="Odometer" tid="in-odometer" type="number" value={extra.odometer} onChange={(e)=>setExtra({...extra,odometer:e.target.value})}/>
      </TwoCol>
      <TwoCol>
        <F label="Stock #" tid="in-stock" value={extra.stock_number} onChange={(e)=>setExtra({...extra,stock_number:e.target.value})}/>
        <F label="Auction / Lot #" tid="in-lot" value={extra.auction_lot} onChange={(e)=>setExtra({...extra,auction_lot:e.target.value})}/>
      </TwoCol>
      <F label="Pickup address" tid="in-pickup-addr" value={extra.pickup_address} onChange={(e)=>{setExtra({...extra,pickup_address:e.target.value}); checkLocation(e.target.value);}}/>
      {locAlert?.has_alert && <LocationAlert loc={locAlert}/>}
      <F label="Delivery address" tid="in-delivery-addr" value={extra.delivery_address} onChange={(e)=>setExtra({...extra,delivery_address:e.target.value})}/>
      {canMoney && <F label="Rate for this vehicle (USD)" tid="in-vehicle-rate" type="number" value={extra.rate} onChange={(e)=>setExtra({...extra,rate:e.target.value})}/>}
      <button data-testid="btn-save-vehicle" onClick={save} className="btn-amber w-full">Save Vehicle</button>
    </div>
  </AppShell>;
}

export function LocationAlert({ loc }) {
  return <div className="border-l-4 border-accent bg-accent/10 p-4 slide-up" data-testid="location-alert">
    <div className="eyebrow text-accent mb-1">HaulGuard location alert</div>
    <div className="font-display font-bold">"{loc.alert_message}"</div>
    <div className="text-xs text-muted-foreground font-mono-tech mt-1">{loc.name} · {loc.address}</div>
  </div>;
}

// Shared tiny components
function Header({ title, back, cta }) {
  return <div className="flex items-center justify-between mb-6">
    <div>
      {back && <Link to={back} className="eyebrow text-accent hover:underline" data-testid="btn-back">← Back</Link>}
      <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">{title}</h1>
    </div>
    {cta}
  </div>;
}
function Info({ k, v }) { return <div className="bg-white border border-border/60 p-3"><div className="eyebrow text-muted-foreground">{k}</div><div className="font-display font-bold">{v || "—"}</div></div>; }
function TwoCol({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>; }
function F({ label, tid, value, onChange, type="text", textarea }) {
  return <label className="block">
    <span className="eyebrow block mb-1">{label}</span>
    {textarea ? (
      <textarea data-testid={tid} value={value} onChange={onChange} rows={3} className="w-full border-2 border-border focus:border-accent px-3 py-2 outline-none"/>
    ) : (
      <input data-testid={tid} type={type} value={value} onChange={onChange} className="w-full border-2 border-border focus:border-accent px-3 py-2 outline-none"/>
    )}
  </label>;
}
